/**
 * AppellationDrawer — volet flottant (droite) pour ajouter/compléter une appellation viticole.
 * Saisie assistée par recherche internet (IA du fournisseur configuré) : renseigne le vignoble,
 * la sous-région et les cépages classiques par couleur. Enregistrement durable dans l'index.
 * Réutilisable depuis l'index des appellations, la fiche bouteille et le scanner.
 */
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Sparkles, Plus, X, Loader2 } from 'lucide-react';
import { vignoblesApi, caveApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/kit';
import { WINE_COLOR_DOTS } from '../../constants';

const COLOR_OPTIONS = Object.values(WINE_COLOR_DOTS).map((c) => c.label);
const emptyEntry = () => ({ couleur: '', cepages: '', remarques: '' });
// Pays viticoles connus (proposés après les pays déjà présents dans l'index).
export const KNOWN_WINE_COUNTRIES = [
  'France', 'Italie', 'Espagne', 'Portugal', 'Allemagne', 'Autriche', 'Suisse', 'Luxembourg',
  'Grèce', 'Hongrie', 'Géorgie', 'Croatie', 'Slovénie', 'Roumanie', 'Bulgarie', 'Moldavie',
  'Royaume-Uni', 'Liban', 'Israël', 'Maroc', 'Afrique du Sud',
  'États-Unis', 'Canada', 'Mexique', 'Argentine', 'Chili', 'Uruguay', 'Brésil',
  'Australie', 'Nouvelle-Zélande', 'Chine', 'Japon',
];

export function AppellationDrawer({ open, onClose, initialName = '', onSaved, readOnly = false }) {
  const qc = useQueryClient();
  const { isSuperadmin } = useAuth();
  // Les cépages de la cave sont cloisonnés par établissement : inutile (et 403) côté super-admin.
  const { data: knownCepages = [] } = useQuery({ queryKey: ['cepages'], queryFn: caveApi.cepages, enabled: open && !isSuperadmin });
  const { data: existingPays = [] } = useQuery({ queryKey: ['appellation-pays'], queryFn: vignoblesApi.appellationPays, enabled: open });
  const { data: lookup = [] } = useQuery({
    queryKey: ['appellation-lookup', initialName],
    queryFn: () => vignoblesApi.appellations(initialName), enabled: open && !!initialName.trim(),
  });
  const [name, setName] = useState(initialName);
  const [pays, setPays] = useState('France');
  const [vignoble, setVignoble] = useState('');
  const [sousRegion, setSousRegion] = useState('');
  const [entries, setEntries] = useState([emptyEntry()]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // Cépages proposés ajustés au pays (dérivés de l'index des appellations), repli sur les cépages de la cave.
  const { data: paysCepages = [] } = useQuery({
    queryKey: ['appellation-cepages', pays], queryFn: () => vignoblesApi.appellationCepages(pays),
    enabled: open && !!pays.trim(),
  });
  const cepageOptions = paysCepages.length ? paysCepages : knownCepages;

  const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const existing = lookup.find((a) => norm(a.appellation) === norm(initialName));
  // Pays proposés : ceux déjà présents dans l'index d'abord, puis les autres pays viticoles connus.
  const paysOptions = [
    ...existingPays,
    ...KNOWN_WINE_COUNTRIES.filter((c) => !existingPays.some((p) => norm(p) === norm(c))),
  ];

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setName(existing.appellation); setPays(existing.pays || 'France'); setVignoble(existing.vignoble || ''); setSousRegion(existing.sous_region || '');
      const es = (existing.entries || []).filter((e) => e.couleur || e.cepages);
      setEntries(es.length ? es.map((e) => ({
        couleur: COLOR_OPTIONS.find((c) => c.toLowerCase() === (e.couleur || '').toLowerCase()) || e.couleur || '',
        cepages: e.cepages || '', remarques: e.remarques || '',
      })) : [emptyEntry()]);
      setIsEditing(true);
    } else {
      setName(initialName); setPays('France'); setVignoble(''); setSousRegion(''); setEntries([emptyEntry()]); setIsEditing(false);
    }
  }, [open, existing?.appellation, initialName]); // eslint-disable-line react-hooks/exhaustive-deps

  const research = async () => {
    if (!name.trim()) { toast.error('Saisissez d’abord le nom de l’appellation'); return; }
    setBusy(true);
    try {
      const r = await vignoblesApi.enrichAppellation(name.trim());
      if (r.vignoble && !vignoble) setVignoble(r.vignoble);
      if (r.sous_region && !sousRegion) setSousRegion(r.sous_region);
      const fetched = (r.entries || []).filter((e) => e.couleur || e.cepages);
      if (fetched.length) {
        setEntries(fetched.map((e) => ({ couleur: e.couleur || '', cepages: e.cepages || '', remarques: e.remarques || '' })));
      }
      toast.success('Recherche terminée — vérifiez et complétez');
    } catch (e) { toast.error(e.message || 'Recherche impossible'); }
    finally { setBusy(false); }
  };

  const setEntry = (i, key, val) => setEntries((arr) => arr.map((e, idx) => (idx === i ? { ...e, [key]: val } : e)));
  const addEntry = () => setEntries((arr) => [...arr, emptyEntry()]);
  const removeEntry = (i) => setEntries((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) { toast.error('Nom d’appellation requis'); return; }
    setSaving(true);
    try {
      const payload = {
        appellation: name.trim(), pays: pays.trim() || 'France', vignoble: vignoble.trim(), sous_region: sousRegion.trim(),
        entries: entries.filter((e) => e.couleur || e.cepages).map((e) => ({ ...e })),
      };
      const saved = await vignoblesApi.createAppellation(payload);
      qc.invalidateQueries({ queryKey: ['appellations'] });
      qc.invalidateQueries({ queryKey: ['appellation-lookup'] });
      toast.success(isEditing ? 'Appellation mise à jour' : 'Appellation enregistrée');
      onSaved?.(saved);
      onClose();
    } catch (e) { toast.error(e.message || 'Enregistrement impossible'); }
    finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} title={readOnly ? "Appellation" : (isEditing ? "Éditer l'appellation" : 'Ajouter une appellation')} testId="appellation-drawer" width="max-w-lg"
      footer={readOnly ? (
        <div className="flex justify-end"><Button onClick={onClose}>Fermer</Button></div>
      ) : (
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={saving} data-testid="appellation-drawer-save">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      )}>
      {readOnly && <p className="text-[12px] text-n500 mb-3 -mt-1">Index partagé — modifiable uniquement par l'administrateur de la plateforme.</p>}
      <label className="block mb-3">
        <span className="text-[12px] text-n600 block mb-1">Nom de l’appellation</span>
        <div className="flex gap-2">
          <input autoFocus={!readOnly} disabled={readOnly} className="vd-input h-9 flex-1" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="ex. Saint-Joseph" data-testid="appellation-drawer-name" />
          {!readOnly && (
            <Button variant="secondary" className="h-9 shrink-0" onClick={research} disabled={busy} data-testid="appellation-drawer-research">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Rechercher
            </Button>
          )}
        </div>
        {!readOnly && <span className="text-[11px] text-n500">La recherche internet (IA) propose le vignoble, la sous-région et les cépages.</span>}
      </label>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <label className="block">
          <span className="text-[12px] text-n600 block mb-1">Pays</span>
          {readOnly ? <input className="vd-input h-9 w-full" value={pays} disabled /> : <PaysCombo value={pays} onChange={setPays} options={paysOptions} testId="appellation-drawer-pays" />}
        </label>
        <label className="block">
          <span className="text-[12px] text-n600 block mb-1">Vignoble (grande région)</span>
          <input disabled={readOnly} className="vd-input h-9" value={vignoble} onChange={(e) => setVignoble(e.target.value)}
            placeholder="ex. Vallée du Rhône" data-testid="appellation-drawer-vignoble" />
        </label>
        <label className="block">
          <span className="text-[12px] text-n600 block mb-1">Sous-région / département</span>
          <input disabled={readOnly} className="vd-input h-9" value={sousRegion} onChange={(e) => setSousRegion(e.target.value)}
            placeholder="ex. Ardèche" data-testid="appellation-drawer-sousregion" />
        </label>
      </div>

      <p className="kicker mb-2">Couleurs & cépages</p>
      <div className="space-y-2">
        {entries.map((e, i) => (
          <div key={i} className="vd-card p-2.5" data-testid={`appellation-entry-${i}`}>
            {readOnly ? (
              <div className="text-[13.5px]">
                {e.couleur && <span className="text-n500">{e.couleur} — </span>}{e.cepages || '—'}
                {e.remarques ? <div className="text-[12px] text-n500 mt-1">{e.remarques}</div> : null}
              </div>
            ) : (<>
              <div className="flex items-center gap-2 mb-2">
                <select className="vd-input h-8 w-36 text-[13px]" value={e.couleur} onChange={(ev) => setEntry(i, 'couleur', ev.target.value)} data-testid={`appellation-entry-color-${i}`}>
                  <option value="">Couleur…</option>
                  {COLOR_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <span className="flex-1" />
                <button onClick={() => removeEntry(i)} className="text-n500 hover:text-red-700" title="Retirer" data-testid={`appellation-entry-remove-${i}`}><X className="w-4 h-4" /></button>
              </div>
              <CepageChips value={e.cepages} options={cepageOptions} onChange={(v) => setEntry(i, 'cepages', v)} testId={`appellation-entry-cepages-${i}`} />
              <input className="vd-input h-8 text-[13px] w-full mt-2" value={e.remarques} onChange={(ev) => setEntry(i, 'remarques', ev.target.value)}
                placeholder="Remarques (facultatif)" data-testid={`appellation-entry-remarks-${i}`} />
            </>)}
          </div>
        ))}
      </div>
      {!readOnly && (
        <button onClick={addEntry} className="mt-2 text-[13px] text-gold700 hover:underline flex items-center gap-1" data-testid="appellation-entry-add">
          <Plus className="w-4 h-4" /> Ajouter une couleur
        </button>
      )}
    </Drawer>
  );
}

/** Sélection/ajout de cépages : choix dans une liste connue + ajout libre, affichés en puces. */
function CepageChips({ value, options, onChange, testId }) {
  const [txt, setTxt] = useState('');
  const list = (value || '').split(',').map((s) => s.trim()).filter(Boolean);
  const add = (c) => { const v = (c || '').trim(); if (v && !list.some((x) => x.toLowerCase() === v.toLowerCase())) onChange([...list, v].join(', ')); };
  const remove = (c) => onChange(list.filter((x) => x !== c).join(', '));
  const available = options.filter((o) => !list.some((x) => x.toLowerCase() === o.toLowerCase()));
  return (
    <div data-testid={testId}>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {list.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 bg-gold100 text-gold800 rounded-full px-2 py-0.5 text-[12px]" data-testid={`${testId}-chip-${c}`}>
              {c}<button onClick={() => remove(c)} className="hover:text-red-700" title="Retirer"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <select className="vd-input h-8 w-40 text-[13px]" value="" onChange={(e) => { add(e.target.value); e.target.value = ''; }} data-testid={`${testId}-select`}>
          <option value="">Choisir un cépage…</option>
          {available.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input className="vd-input h-8 flex-1 text-[13px]" placeholder="ou saisir puis Entrée" value={txt}
          onChange={(e) => setTxt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(txt); setTxt(''); } }}
          data-testid={`${testId}-input`} />
      </div>
    </div>
  );
}

/** Champ Pays avec menu d'autocomplétion (même style qu'Appellation / Région). Réutilisable. */
export function PaysCombo({ value, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const matches = options.filter((o) => (!value.trim() ? true : norm(o).includes(norm(value)))).slice(0, 20);
  return (
    <div className="relative">
      <input className="vd-input h-9 w-full" value={value} data-testid={testId} placeholder="ex. France"
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)} />
      {open && matches.length > 0 && (
        <div className="absolute z-30 mt-1 bg-paper border border-hair rounded shadow-md w-full max-h-56 overflow-auto" data-testid={`${testId}-menu`}>
          {matches.map((o) => (
            <button key={o} type="button" onMouseDown={() => { onChange(o); setOpen(false); }}
              className="block w-full text-left px-2.5 py-1.5 text-[13px] hover:bg-surface" data-testid={`${testId}-opt-${o}`}>{o}</button>
          ))}
        </div>
      )}
    </div>
  );
}
